import { useRef, useState, useEffect } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import axios from '../api/axios';

// Material UI Imports
import Tooltip from '@mui/material/Tooltip';
import Snackbar from '@mui/material/Snackbar';
import Alert from '@mui/material/Alert';

// Custom CSS (Ensure your tooltip styles are defined here or globally)
// import styles from './ChangePassword.module.css'; 

const PWD_REGEX = /^(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9])(?=.*[!@#$%]).{5,24}$/;
const TOKEN_REGEX = /^[A-Z0-9]{64}$/;
const CHANGE_PASSWORD_URL = '/changePassword';

const ChangePassword = () => {
    const { requestId, requestToken } = useParams();
    const navigate = useNavigate();
    const errRef = useRef();

    const [pwd, setPwd] = useState('');
    const [validPwd, setValidPwd] = useState(false);
    const [pwdFocus, setPwdFocus] = useState(false);

    const [matchPwd, setMatchPwd] = useState('');
    const [validMatch, setValidMatch] = useState(false);
    const [matchFocus, setMatchFocus] = useState(false);

    const [showPasswords, setShowPasswords] = useState(false);
    const [success, setSuccess] = useState(false);
    const [loading, setLoading] = useState(false);

    // Snackbar state for global error/success feedback
    const [alertStatus, setAlertStatus] = useState({
        open: false,
        severity: 'info',
        message: '',
    });

    // Initial Token Validation
    useEffect(() => {
        if (!TOKEN_REGEX.test(requestToken)) {
            navigate('/login', { replace: true });
        }
    }, [requestToken, navigate]);

    // Validation Logic
    useEffect(() => {
        setValidPwd(PWD_REGEX.test(pwd));
        setValidMatch(pwd === matchPwd && matchPwd !== '');
    }, [pwd, matchPwd]);

    const handleAlertClose = (event, reason) => {
        if (reason === 'clickaway') return;
        setAlertStatus(prev => ({ ...prev, open: false }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        // Defensive check
        if (!PWD_REGEX.test(pwd) || pwd !== matchPwd) {
            setAlertStatus({
                open: true,
                severity: "error",
                message: "Please ensure all password requirements are met."
            });
            return;
        }

        setLoading(true);
        try {
            await axios.post(CHANGE_PASSWORD_URL,
                JSON.stringify({
                    password: pwd,
                    requestId,
                    requestToken
                }),
                {
                    headers: { 'Content-Type': 'application/json' }
                }
            );
            setSuccess(true);
        } catch (err) {
            let message = 'Our apologies, but we were not able to change your password.';
            if (!err?.response || err.response?.status === 500) {
                message = "We're experiencing technical difficulties. Please try again later.";
            } else if (err.response?.status === 401) {
                message = 'The reset link has expired. Please request a new one.';
            }

            setAlertStatus({
                open: true,
                severity: "error",
                message: message
            });
            errRef.current?.focus();
        } finally {
            setLoading(false);
        }
    };

    // Tooltip Configuration
    const tooltipSlotProps = {
        popper: { sx: { zIndex: 9999 } },
        arrow: { sx: { color: '#7d1327' } }, // Matching your red error theme
        tooltip: {
            sx: {
                backgroundColor: '#7d1327',
                color: '#fff',
                fontSize: '0.75rem',
                padding: '0.5rem',
                borderRadius: '4px',
                boxShadow: '0px 2px 8px rgba(0,0,0,0.3)',
                fontWeight: '300',
                maxWidth: '240px',
            }
        }
    };

    return (
        <>
            {success ? (
                <section style={{ maxWidth: '480px' }}>
                    <h1>Success!</h1>
                    <p>Your password has been successfully changed.</p>
                    <p><Link to="/login">Sign In</Link></p>
                </section>
            ) : (
                <section style={{ maxWidth: '480px' }}>
                    <h1>Change Password</h1>
                    <form onSubmit={handleSubmit}>

                        {/* New Password Field */}
                        <label htmlFor="password">New password:</label>
                        <Tooltip
                            title={
                                <span>
                                    5 to 24 characters.<br /><br />
                                    Must include uppercase and lowercase letters, a number and a special character.<br /><br />
                                    Allowed special characters:<br />! @ # $ %
                                </span>
                            }
                            open={pwdFocus && !validPwd && pwd.length > 0}
                            placement="right"
                            arrow
                            disableHoverListener
                            disableFocusListener
                            disableTouchListener
                            slotProps={tooltipSlotProps}
                        >
                            <input
                                type={showPasswords ? "text" : "password"}
                                id="password"
                                className={(validPwd || pwd === "") ? "" : "invalid"}
                                onChange={(e) => setPwd(e.target.value)}
                                value={pwd}
                                required
                                onFocus={() => setPwdFocus(true)}
                                onBlur={() => setPwdFocus(false)}
                                autoComplete="new-password"
                            />
                        </Tooltip>

                        {/* Confirm Password Field */}
                        <label htmlFor="confirm_pwd">Confirm new password:</label>
                        <Tooltip
                            title="Must match the first password."
                            open={matchFocus && !validMatch && matchPwd.length > 0}
                            placement="right"
                            arrow
                            disableHoverListener
                            disableFocusListener
                            disableTouchListener
                            slotProps={tooltipSlotProps}
                        >
                            <input
                                type={showPasswords ? "text" : "password"}
                                id="confirm_pwd"
                                className={(validMatch || matchPwd === "") ? "" : "invalid"}
                                onChange={(e) => setMatchPwd(e.target.value)}
                                value={matchPwd}
                                required
                                onFocus={() => setMatchFocus(true)}
                                onBlur={() => setMatchFocus(false)}
                                autoComplete="new-password"
                            />
                        </Tooltip>

                        {/* Visibility Toggle */}
                        <div className="checkbox-and-label" style={{ marginTop: '1rem' }}>
                            <input
                                type="checkbox"
                                id="password_visibility"
                                onChange={(e) => setShowPasswords(e.target.checked)}
                            />
                            <label htmlFor="password_visibility">Show passwords</label>
                        </div>

                        <button
                            disabled={!validPwd || !validMatch || loading}
                            style={{ marginTop: '1.5rem' }}
                        >
                            {loading ? "Saving..." : "Change password"}
                        </button>
                    </form>

                    <nav style={{ marginTop: '2rem' }}>
                        <Link to="/resetpassword">Request password reset link</Link>
                        <Link to="/login">Sign in</Link>
                    </nav>

                    {/* Hidden ref for accessibility focus if needed */}
                    <span ref={errRef} tabIndex="-1" />
                </section>
            )}

            <Snackbar
                open={alertStatus.open}
                autoHideDuration={6000}
                onClose={handleAlertClose}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            >
                <Alert onClose={handleAlertClose} variant="filled" severity={alertStatus.severity} sx={{ width: '100%' }}>
                    {alertStatus.message}
                </Alert>
            </Snackbar>
        </>
    );
};

export default ChangePassword;